import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGateOpen } from '../../hooks/useGateOpen';
import { watchIncomingCallDismissals } from '../../services/incoming-call-notification';
import {
  useActiveCohortSessions,
  useActiveCohortSessionsStore,
} from '../../stores/active-cohort-sessions.store';
import { useAuthStore } from '../../stores/auth.store';
import { callTapFromSearch, useIncomingCallStore } from '../../stores/incoming-call.store';
import { useRingStore } from '../../stores/ring.store';
import { isLiveSessionPath } from '../../utils/liveSessionPath';
import { QuietAppNotices } from '../notices/quietZone';
import AvailabilityNoticeToast from './AvailabilityNoticeToast';
import IncomingCallSheet, { CallerGaveUpSheet } from './IncomingCallSheet';
import RingSheet from './RingSheet';

/**
 * App-wide, beside CircleStartBanner: the listener's side of the shared poll
 * (P7d). Subscribes to the SAME refcounted poll as the banner, under the
 * same condition (not in a live session), so no second poll exists.
 *
 * Shows the ring unless this device already handled it (Not now, a push
 * tap that took over, expiry), and waits behind any open gate. The notice
 * toast never competes with a ring.
 *
 * R11: the seeker's side of the same poll too: a call waiting for them
 * (IncomingCallSheet), on every page this host runs on, unless this device
 * already handled that session or the seeker's call screen is up and the
 * call is a plain match (that screen opens it itself). R14: a CLAIM rings
 * whatever screen is up, the 1:1 waiting screen included (no screen
 * connects it; only Join does). Their own call comes before a ring. Then
 * "{name} couldn't wait" (the poll's one-shot missed_call) when no call is
 * ringing.
 */
export default function ListenerRingHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const enabled = isAuthenticated && !isLiveSessionPath(location.pathname);
  useActiveCohortSessions(enabled);

  // RING-PUSH spec 4: the call notification goes once the call is settled.
  useEffect(() => watchIncomingCallDismissals(), []);

  // RING-PUSH spec 2: a call push's landing (notification-router
  // incomingCallRoute: `call` = the session, `call_action` = a button on
  // the notification). The tap is held as soon as it is seen, signed in or
  // not (#1009 item 4: a cold start's landing can be lost on the way to the
  // sign-in), and run ONCE when signed in: poll NOW so the sheet shows for
  // that session within one round trip, and hand the action to that
  // session's sheet. Signed in, the flags also leave the address bar so a
  // reload or Back never re-runs them (signed out they stay, for the
  // sign-in's redirect). A session that is over never shows a sheet: the
  // poll brings its "couldn't wait" card, or nothing.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('call');
    const tap = callTapFromSearch(location.search);
    if (tap !== null) useIncomingCallStore.getState().holdPushTap(tap);
    if (!isAuthenticated) return;
    const held = useIncomingCallStore.getState().takeHeldPushTap();
    if (held !== null) {
      useIncomingCallStore.getState().setPushTap(held);
      useActiveCohortSessionsStore.getState().pollNow();
    }
    if (raw === null) return;
    params.delete('call');
    params.delete('call_action');
    const search = params.toString();
    void navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true, state: location.state },
    );
  }, [location.search, location.pathname, location.state, isAuthenticated, navigate]);

  const ring = useActiveCohortSessionsStore((s) => s.ring);
  const notice = useActiveCohortSessionsStore((s) => s.notice);
  const handled = useRingStore((s) => s.handled);
  const incomingCall = useActiveCohortSessionsStore((s) => s.incomingCall);
  const missedCall = useActiveCohortSessionsStore((s) => s.missedCall);
  const dismissMissed = useActiveCohortSessionsStore((s) => s.dismissMissed);
  const callHandled = useIncomingCallStore((s) => s.handled);
  const callScreenUp = useIncomingCallStore((s) => s.callScreens > 0);
  const gateOpen = useGateOpen();
  const pushTap = useIncomingCallStore((s) => s.pushTap);

  if (!enabled) return null;

  const showCall =
    incomingCall !== null &&
    !callHandled[incomingCall.session_id] &&
    (incomingCall.kind === 'claim' || !callScreenUp);
  const showMissed = !showCall && missedCall !== null;
  const showRing = !showCall && !showMissed && ring !== null && !handled[ring.id];

  return (
    <>
      {showCall ? (
        <IncomingCallSheet
          key={incomingCall.session_id}
          call={incomingCall}
          hidden={gateOpen}
          pushAction={
            pushTap?.sessionId === incomingCall.session_id && pushTap.action !== 'open'
              ? pushTap.action
              : undefined
          }
        />
      ) : null}
      {showMissed ? (
        <CallerGaveUpSheet
          key={missedCall.session_id}
          missed={missedCall}
          hidden={gateOpen}
          onClose={dismissMissed}
        />
      ) : null}
      {showRing ? <RingSheet key={ring.id} ring={ring} hidden={gateOpen} /> : null}
      {/* A ring or a call sheet on screen: app notices wait. */}
      {showCall || showMissed || showRing ? <QuietAppNotices /> : null}
      {notice !== null && !showRing && !showCall && !showMissed ? (
        <AvailabilityNoticeToast key={notice.key} notice={notice} />
      ) : null}
    </>
  );
}
